/**
 * Dazm: variant fallback for products where not every option combination exists.
 *
 * Example: "Plate" is sold in Black only for some iPhone models. If a shopper picks
 * Black while a model that has no Black version is selected, the theme would show
 * "Unavailable" with no price. Instead, this script moves the other options to the
 * closest variant that does exist (keeping as many of the current choices as
 * possible, and preferring variants that are in stock) before the theme's
 * variant picker requests the new selection.
 */

/** @type {Map<string, Promise<any> | null>} */
const requests = new Map();
/** @type {Map<string, any>} */
const products = new Map();

/** @param {HTMLElement} picker */
function productUrlOf(picker) {
  return (picker.dataset.productUrl || '').split('?')[0];
}

/** @param {HTMLElement} picker */
function load(picker) {
  const url = productUrlOf(picker);
  if (!url || products.has(url) || requests.has(url)) return;

  const request = fetch(`${url}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => {
      if (product && Array.isArray(product.variants)) products.set(url, product);
      requests.delete(url);
    })
    .catch(() => requests.delete(url));

  requests.set(url, request);
}

/**
 * Picks the variant to fall back to.
 * @param {any} product - Product JSON from /products/<handle>.js
 * @param {number} optionIndex - Index of the option the shopper just changed
 * @param {string} value - The value the shopper picked
 * @param {Record<string, string>} current - Current selections by option name
 * @returns {any | null}
 */
export function pickFallbackVariant(product, optionIndex, value, current) {
  const names = product.options.map((/** @type {any} */ option) => (typeof option === 'string' ? option : option.name));
  const valueLists = product.options.map((/** @type {any} */ option) => (Array.isArray(option?.values) ? option.values : []));
  let best = null;
  let bestScore = -1;
  let bestOrder = Infinity;

  for (const variant of product.variants) {
    if (variant.options[optionIndex] !== value) continue;

    // In stock first, then keep as many of the shopper's other choices as possible.
    let score = variant.available ? 100 : 0;
    // Ties go to the variant whose other values come first in the option lists,
    // i.e. the order the buttons are shown on the page.
    let order = 0;
    names.forEach((/** @type {string} */ name, /** @type {number} */ index) => {
      if (index === optionIndex) return;
      if (current[name] === variant.options[index]) score += 1;
      const position = valueLists[index].indexOf(variant.options[index]);
      order = order * 1000 + (position === -1 ? 999 : position);
    });

    if (score > bestScore || (score === bestScore && order < bestOrder)) {
      best = variant;
      bestScore = score;
      bestOrder = order;
    }
  }

  return best;
}

/** @param {Event} event */
function onChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

  const picker = input.closest('variant-picker');
  if (!(picker instanceof HTMLElement)) return;

  // The theme only sets data-variant-id when this value + the other current
  // selections form a real variant. If it is set, there is nothing to fix.
  if (input.dataset.variantId) return;

  const product = products.get(productUrlOf(picker));
  if (!product) return;

  const optionName = input.dataset.optionName;
  const names = product.options.map((/** @type {any} */ option) => (typeof option === 'string' ? option : option.name));
  const optionIndex = names.indexOf(optionName);
  if (optionIndex === -1) return;

  /** @type {Record<string, string>} */
  const current = {};
  picker.querySelectorAll('fieldset input:checked').forEach((checked) => {
    if (checked instanceof HTMLInputElement && checked.dataset.optionName) {
      current[checked.dataset.optionName] = checked.value;
    }
  });

  const variant = pickFallbackVariant(product, optionIndex, input.value, current);
  if (!variant) return;

  names.forEach((/** @type {string} */ name, /** @type {number} */ index) => {
    if (index === optionIndex) return;

    const target = Array.from(picker.querySelectorAll('fieldset input[type="radio"]')).find(
      (radio) =>
        radio instanceof HTMLInputElement &&
        radio.dataset.optionName === name &&
        radio.value === variant.options[index]
    );

    if (target instanceof HTMLInputElement && !target.checked) {
      target.checked = true;
    }
  });

  // Lets the theme put the right ?variant= in the URL.
  input.dataset.variantId = String(variant.id);
}

function preloadAll() {
  document.querySelectorAll('variant-picker[data-product-url]').forEach((picker) => {
    if (picker instanceof HTMLElement) load(picker);
  });
}

// Capture phase on document runs before the theme's variant-picker listener.
document.addEventListener('change', onChange, true);

// Quick-add dialogs and product cards are added later; load their data on first hover/focus.
['pointerover', 'focusin'].forEach((type) => {
  document.addEventListener(
    type,
    (event) => {
      const picker = event.target instanceof Element ? event.target.closest('variant-picker[data-product-url]') : null;
      if (picker instanceof HTMLElement) load(picker);
    },
    { passive: true }
  );
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', preloadAll);
} else {
  preloadAll();
}
